import * as React from "react"
import { connect, Provider } from "react-redux"
import trans from "trans"

import {
    css,
    Select,
    Range,
    modal,
    Button,
    Grid,
    GridCell,
    Checkbox,
    Icon,
    PopupHint,
    TextInput,
} from "@bdfx/mergado-ui-kit"

import { UPDATE_SETTINGS, } from "template-edit/reducers"
import { Project, SettingsFormType } from "app/types/model-types";
import {
    updateInputValue as change,
} from "app/reducers/form";

import { SettingsSpinner } from "./spinner";

import {
    saveProjectSettings as apiSaveProjectSettings,
    getRandomPreviewImage as apiGetRandomPreviewImage,
    saveContactEmail,
} from "app/helpers/api";

import store from "store";
import { createToast } from "actions";
import toastFactory from "app/helpers/toast_factory";
import debounce from "lodash/debounce";
import { ID, ReduxList } from "app/types/app-types";
import SettingsModalWrapper from "./settings-modal-wrapper";
import { MaxProductsPerPage } from "products/components/product-table";
import { iconDisabledColor } from "template-edit/components/tools-panel";
import { AxiosResponse } from "axios";
import { ALL_OUTPUT_FORMATS_WITHOUT_GIF, COMPRESSIBLE_FORMATS } from "shape-edit/forms/helpers";
import { selectFormValues } from "app/reducers/form";
import { setProjectToStore } from "app/reducers/project";
import Content from "first-steps/content"
import AdvancedProjectSettings from "./advanced-project-settings"
import ProjectCategories from "./project-categories"
import { RouterType, withRouter } from "app/components/routes"
import { testUsers } from "template-edit/components/helper"

export type PreviewImageType = {
    image: string
    info: any
    template: string
    width: number
    height: number
    contentLength: number
    productId: ID
}

interface Props {
    change: typeof change
    settingsForm: SettingsFormType
    project: ReduxList<Project>
    router: RouterType
}

interface State {
    isLoading: boolean
    isLoaded: boolean
    previewImage: PreviewImageType
    previewProductId: ID
    isLoadingPreview: boolean
    originalContentLength: number
    originalFormat: string
    originalCompression: number
    contactEmail: string
    isContactEmailInvalid: boolean
}

export const autoCompressionOptions = [
    { value: `${100 * 1024}`, label: '100kB (SEO)' },
    { value: `${200 * 1024}`, label: '200kB (SEO)' },
    { value: `${400 * 1024}`, label: '400kB (SEO)' },
    { value: `${2 * 1024 * 1024}`, label: '2MB (Heureka, Mall, Nabava)' },
    { value: `${5 * 1024 * 1024}`, label: '5MB (Alza, Modio, Domodi)' },
    { value: `${8 * 1024 * 1024}`, label: '8MB (Instagram, eMAG)' },
    { value: `${16 * 1024 * 1024}`, label: '16MB (Google Merchant, ÁrGép)' },
    { value: `${20 * 1024 * 1024}`, label: '20MB (Pinterest)' },
    { value: `${30 * 1024 * 1024}`, label: '30MB (Facebook 1:1)' },
]

class ProjectSettings extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props)

        this.state = {
            isLoaded: false,
            isLoading: false,
            previewImage: null,
            previewProductId: null,
            isLoadingPreview: false,
            originalContentLength: null,
            originalFormat: null,
            originalCompression: null,
            contactEmail: props.project.isLoaded ? props.project.data.contact_email : null,
            isContactEmailInvalid: null,
        }
    }

    handleImageRefresh = (close?: Function) => {
        this.setState({ isLoadingPreview: true })
        this.refreshDebounced(close)
    }

    refreshNow = (newProductId = false, clearOriginalContentLength = false, close?: Function) => {
        const { output_image_format, output_image_compression } = this.props.settingsForm

        if (clearOriginalContentLength) {
            this.setState({ previewProductId: null, originalContentLength: null, originalFormat: null, originalCompression: null })
        }

        this.setState({ isLoadingPreview: true })

        apiGetRandomPreviewImage(output_image_format, output_image_compression, newProductId ? null : this.state.previewProductId)
            .then((r: AxiosResponse<PreviewImageType>) => {
                this.setState({
                    previewImage: r.data,
                    previewProductId: r.data.productId,
                })

                if (this.state.originalContentLength === null && r.data.contentLength) {
                    this.setState({
                        originalContentLength: r.data.contentLength,
                        originalCompression: output_image_compression,
                        originalFormat: output_image_format
                    })
                }

                if (typeof close === 'function') {
                    close()
                    this.showPreviewModal(r.data, this.state.originalContentLength === null && r.data.contentLength ? r.data.contentLength : this.state.originalContentLength)
                }
            }).catch(e => {
                this.setState({ previewImage: null })
            }).finally(() => {
                this.setState({ isLoadingPreview: false })
            })
    }

    refreshDebounced = debounce((close?: Function) => {
        this.refreshNow(false, false, close)
    }, 2200)

    componentDidMount() {
        this.refreshNow()
    }

    onSearch = (phrase, option) => typeof option.name === "string" ? option.name.toLowerCase().includes(phrase.toLowerCase()) : true
    highlightTextGetter = item => typeof item.name === "string" ? item.name : "-"

    showFirstStepsGuide = () => {
        modal({
            closeOnEscape: false,
            closeOnClickOutside: false,
            fullscreen: true,
            hideCloseButton: true,
            message: (close) => <Provider store={store}>
                <Content close={close} router={this.props.router} />
            </Provider>
        })
    }

    showPreviewModal = (previewImage, originalContentLength) => {
        modal({
            title: trans('output_image_preview'),
            overlayClassName: 'image-preview-wrapper-modal',
            closeOnEscape: true,
            closeOnClickOutside: true,
            message: (close) => <Provider store={store}>
                <SettingsModalWrapper
                    close={close}
                    previewImage={previewImage}
                    originalContentLength={this.state.originalContentLength}
                    originalCompression={this.state.originalCompression}
                    originalFormat={this.state.originalFormat}
                    refreshNow={(newProductId = false, clearOriginalContentLength = false) => this.refreshNow(newProductId, clearOriginalContentLength, close)}
                    refreshDebounced={this.handleImageRefresh}
                />
            </Provider>
        })
    }

    isContactEmailInvalid = (email: string): boolean => {
        if (email === null || email === '') {
            this.setState({isContactEmailInvalid: false})
            return false
        }

        const isInvalid = !email.match(/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)
        this.setState({isContactEmailInvalid: isInvalid})
        return isInvalid
    }

    saveContactEmail = (contactEmail: string) => {
        const { project } = this.props
        const sameAsBefore = project.isLoaded && contactEmail !== project.data.contact_email
        const isValid = !this.isContactEmailInvalid(contactEmail)

        if (sameAsBefore && isValid) {
            saveContactEmail(contactEmail)
                .then((response: AxiosResponse<Project>) => {
                    this.setState({ contactEmail: null })
                    store.dispatch(setProjectToStore(response.data))
                })
                .catch(error => {
                    console.error('cannot save contact email', error)
                })
        }
    }

    saveProjectSettings = () => {
        apiSaveProjectSettings(this.props.settingsForm)
            .then((response: AxiosResponse<SettingsFormType>) => {
                if (this.state.previewImage && this.state.originalContentLength && this.state.previewImage.contentLength !== this.state.originalContentLength) {
                    this.setState({
                        originalContentLength: this.state.previewImage.contentLength,
                        originalFormat: response.data.output_image_format,
                        originalCompression: response.data.output_image_compression
                    })
                }
            })
            .catch(error => {
                store.dispatch(createToast(toastFactory(UPDATE_SETTINGS.ERROR, error)))
            })
    }

    saveContactEmailDebounced = debounce((contactEmail: string) => {
        this.saveContactEmail(contactEmail)
    }, 1000)

    saveProjectSettingsDebounced = debounce(() => {
        this.saveProjectSettings()
    }, 650)

    render() {
        const { isLoading, isLoadingPreview, previewImage, originalContentLength, contactEmail, isContactEmailInvalid } = this.state

        if (isLoading) {
            return (
                <SettingsSpinner />
            )
        }

        const { project, settingsForm } = this.props
        const { output_image_format, auto_compression_to_size, disable_mailgun_notifications, auto_template_priority, force_remove_alpha } = settingsForm

        const showCompression = COMPRESSIBLE_FORMATS.includes(output_image_format)
        const lessThanOriginal = previewImage && previewImage.contentLength && originalContentLength ? ((originalContentLength - previewImage.contentLength) * 100 / originalContentLength).toFixed(0) : null

        const optionsItemsPerPage = [3, 4, 6, 8, 9, 12, 16, 18, 24, 32].map((o: number) => ({
            label: `${o}`,
            value: `${o}`
        }))

        const userId = project.data?.user?.id ? project.data.user.id : null

        return (
            <React.Fragment>
                <StyledGrid>
                    <GridCell>{trans('products_per_page')}</GridCell>
                    <GridCell>
                        <Select
                            small={true}
                            value={`${this.props.settingsForm.products_per_page}`}
                            className={'products-per-page'}
                            options={optionsItemsPerPage}
                            onChange={(e) => {
                                this.props.change('settingsForm', 'products_per_page', e)
                                this.saveProjectSettingsDebounced()
                            }}
                        />
                    </GridCell>

                    <GridCell>{trans('products_per_page_on_list')}</GridCell>
                    <GridCell>
                        <TextInput
                            small={true}
                            type={'number'}
                            min={1}
                            max={MaxProductsPerPage}
                            name='settingsForm.products_per_page_on_list'
                            placeholder={trans('products_per_page_on_list')}
                            onChange={() => this.saveProjectSettingsDebounced()}
                        />
                    </GridCell>

                    <GridCell>{trans('output_image_format')}</GridCell>
                    <GridCell>
                        <Select
                            small={true}
                            name='settingsForm.output_image_format'
                            options={ALL_OUTPUT_FORMATS_WITHOUT_GIF.map(outputFromat => ({ value: outputFromat, label: outputFromat }))}
                            onChange={() => {
                                this.handleImageRefresh()
                                this.saveProjectSettingsDebounced()
                            }}
                        />
                    </GridCell>

                    {showCompression &&
                        <React.Fragment>
                            <GridCell>{trans('output_image_compression')}</GridCell>
                            <GridCell className="image-compression">
                                <Range
                                    small={true}
                                    name={'settingsForm.output_image_compression'}
                                    min={10}
                                    max={100}
                                    step={5}
                                    onChange={() => {
                                        this.handleImageRefresh()
                                        this.saveProjectSettingsDebounced()
                                    }}
                                />
                            </GridCell>
                        </React.Fragment>
                    }

                    {showCompression &&
                        <React.Fragment>
                            <GridCell>{trans('auto_compression_to_size')}</GridCell>
                            <GridCell>
                                <Select
                                    small={true}
                                    value={auto_compression_to_size}
                                    options={autoCompressionOptions}
                                    onChange={(value) => {
                                        this.props.change('settingsForm', 'auto_compression_to_size', value)
                                        this.saveProjectSettingsDebounced()
                                    }}
                                />
                            </GridCell>
                        </React.Fragment>
                    }

                    <GridCell>
                        {trans('short_url_with_extension')}&nbsp; <PopupHint icon={<Icon type={'info-circle'} color={iconDisabledColor} />}>{trans("short_url_with_extension_info")}</PopupHint>
                    </GridCell>
                    <GridCell>
                        <Checkbox
                            small={true}
                            name="settingsForm.short_url_with_extension"
                            onChange={(value) => {
                                this.props.change('settingsForm', 'short_url_with_extension', value)
                                this.saveProjectSettingsDebounced()
                            }}
                        />
                    </GridCell>

                    <GridCell>
                        {trans('skip_refresh_images')}&nbsp; <PopupHint icon={<Icon type={'info-circle'} color={iconDisabledColor} />}>{trans("skip_refresh_images_hint")}</PopupHint>
                    </GridCell>
                    <GridCell>
                        <Checkbox
                            small={true}
                            name="settingsForm.skip_refresh_images"
                            onChange={(value) => {
                                this.props.change('settingsForm', 'skip_refresh_images', value)
                                this.saveProjectSettingsDebounced()
                            }}
                        />
                    </GridCell>

                    <GridCell>{trans('group_by_image')}</GridCell>
                    <GridCell>
                        <Checkbox
                            small={true}
                            name="settingsForm.group_by_image"
                            onChange={(value) => {
                                this.props.change('settingsForm', 'group_by_image', value)
                                this.saveProjectSettingsDebounced()
                            }}
                        />
                    </GridCell>

                    <GridCell>{trans('send_mailgun_notifications')}</GridCell>
                    <GridCell>
                        <Checkbox
                            small={true}
                            value={disable_mailgun_notifications ? 0 : 1}
                            onChange={(value) => {
                                this.props.change('settingsForm', 'disable_mailgun_notifications', value ? 0 : 1)
                                this.saveProjectSettingsDebounced()
                            }}
                        />
                    </GridCell>

                    {testUsers.includes(userId) &&
                        <React.Fragment>
                            <GridCell className="some-users-only">{trans('force_remove_alpha')}</GridCell>
                            <GridCell className="some-users-only">
                                <Checkbox
                                    small={true}
                                    value={force_remove_alpha ? 1 : 0}
                                    onChange={(value) => {
                                        this.props.change('settingsForm', 'force_remove_alpha', value ? 1 : 0)
                                        this.saveProjectSettingsDebounced()
                                    }}
                                />
                            </GridCell>
                        </React.Fragment>
                    }

                    <GridCell>{trans('auto_template_priority')}</GridCell>
                    <GridCell>
                        <Checkbox
                            small={true}
                            value={auto_template_priority ? 1 : 0}
                            onChange={(value) => {
                                this.props.change('settingsForm', 'auto_template_priority', value ? 1 : 0)
                                this.saveProjectSettingsDebounced()
                            }}
                        />
                    </GridCell>

                    <GridCell key="first_steps_wizard">{trans('first_steps_wizard')}</GridCell>
                    <GridCell>
                        <Button
                            type={'void'}
                            size={'small'}
                            onClick={() => this.showFirstStepsGuide()}
                            icon={<Icon type="edit" text={trans("edit")} />}
                        />
                    </GridCell>

                    {project.isLoaded &&
                        <React.Fragment>
                            <GridCell>{trans('contact_email')}</GridCell>
                            <GridCell>
                                <TextInput
                                    small={true}
                                    type="email"
                                    placeholder={"@"}
                                    value={contactEmail !== null ? contactEmail : project.data.contact_email}
                                    onChange={(email) => {
                                        this.setState({ contactEmail: email })
                                        this.saveContactEmailDebounced(email)
                                    }}
                                    invalid={isContactEmailInvalid}
                                    error={isContactEmailInvalid ? trans('invalid_email') : undefined}
                                    onClear={() => {
                                        this.setState({ contactEmail: '' })
                                        this.saveContactEmail(null)
                                    }}
                                />
                            </GridCell>
                        </React.Fragment>
                    }

                    <ProjectCategories />
                </StyledGrid>

                <StyledGrid className={`${isLoading ? 'muk-helper-loading' : ''}`}>
                    <GridCell style={{ textAlign: 'center !important' }}>
                        <Grid cols={'auto'} gap={'5px'} style={{ width: 'fit-content', display: 'inline-grid' }}>
                            {!isLoadingPreview && previewImage && previewImage.image &&
                                <React.Fragment>
                                    <GridCell style={{ textAlign: 'center !important' }}>
                                        <b>{trans('output_image_preview')}</b>
                                    </GridCell>
                                    <GridCell valign={'center'} style={{ lineHeight: '1.5em' }}>
                                    <div><span className={'framed'}>{trans('template_name')}</span>: {previewImage.template}</div>
                                    <div><span className={'framed'}>{trans('size_format')}</span>: {previewImage.width} x {previewImage.height}</div>
                                    <div><span className={'framed'}>{trans('output_image_content_length')}</span>: {this.state.previewImage.contentLength} kB</div>

                                    {this.state.originalContentLength && this.state.previewImage.contentLength !== this.state.originalContentLength &&
                                        <>
                                            <div>({trans('original_output_image_content_length')}: {this.state.originalContentLength} kB, {this.state.originalFormat}
                                                {this.state.originalFormat === 'jpeg' &&
                                                    ', ' + this.state.originalCompression
                                                })
                                            </div>
                                            <div>{trans('about')} {lessThanOriginal} % {parseInt(lessThanOriginal.toString()) > 0 ? trans('less_than_original') : trans('more_than_original')}</div>
                                        </>
                                    }
                                </GridCell>
                                </React.Fragment>
                            }

                            <GridCell valign={'end'} style={{ paddingBottom: '20px', paddingTop: '20px', textAlign: 'center !important' }}>
                                {!isLoadingPreview ?
                                    <Button
                                        type={'void'}
                                        size={'small'}
                                        icon={<Icon type={'refresh'} text={trans('random_product')} />}
                                        onClick={() => this.refreshNow(true, true)}
                                    /> : previewImage ? <SettingsSpinner /> : <span />
                                }
                            </GridCell>
                        </Grid>
                    </GridCell>

                    <GridCell className={isLoadingPreview ? 'muk-helper-loading-line with-line' : ''}>
                        {previewImage && previewImage.image &&
                            <img
                                src={previewImage.image}
                                style={{ maxWidth: '100%', maxHeight: 'auto', cursor: 'pointer' }}
                                onClick={() => this.showPreviewModal(previewImage, originalContentLength)}
                            />
                        }
                    </GridCell>
                </StyledGrid>

                <AdvancedProjectSettings />
            </React.Fragment>
        )
    }
}

export const StyledGrid = css(Grid)({
    gridTemplateColumns: '1fr 1fr',
    gridColumnGap: '20px',
    '@media screen and (max-width: 800px)': {
        gridTemplateColumns: '1fr',
        " img.main-image": {
            maxWidth: "45vw",
        },
    },
    paddingTop: '25px',
    ' .muk-gridcell': {
        alignSelf: 'center',
        textAlign: 'left',
        padding: '5px',
    },
    ' .image': {
        textAlign: 'center',
    },
    ' .settings': {
        alignSelf: 'start',
        textAlign: 'left',
    },
    '@media (min-width: 1025px)': {
        margin: '5px',
        float: 'left',
    },
    paddingBottom: '20px',
    ' .muk-datacell': {
        padding: '6px',
    },
    ' .muk-form-group': {
        padding: 0,
    },
    ' .info': {
        fill: '#ccc',
    },
    ' .image-compression label.muk-form-label': {
        display: 'inline',
    },
    ' .muk-checkbox-wrapper': {
        marginBottom: '0 !important',
    }
})

function mapStateToProps(state) {
    return {
        settingsForm: selectFormValues(state,'settingsForm'),
        project: state.project,
    }
}

export default connect(mapStateToProps, {
    change,
})(withRouter(ProjectSettings))

